import { useOidcUser } from '@axa-fr/react-oidc';
import { Button } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingAnimation, toastNotifications, Icon } from '@uag/react-core';
import { GetOnboardingRequestDetailResponse } from '@uag/welcome-shared/src/api/models';
import {
    getGetRequestsQueryKey,
    useCreateRequestForExistingCustomer,
    useCreateRequestForNewCustomer,
} from '@uag/welcome-shared/src/api/queries/requests/requests';
import { CustomerSelection, CustomerType } from '@uag/welcome-shared/src/components/customer/customerSelection';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

export const SelectCustomerView = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const customerIdentifier = searchParams.get('customerIdentifier')?.trim() ?? '';
    const companyNameParam = searchParams.get('companyName')?.trim() ?? '';
    const [customerType, setCustomerType] = useState<CustomerType>(CustomerType.Existing);
    const [customerNumber, setCustomerNumber] = useState<string>(customerIdentifier);
    const [companyName, setCompanyName] = useState<string>(companyNameParam);
    const [countryId, setCountryId] = useState<string>('');
    const [countryCode, setCountryCode] = useState<string>('');
    const [isDoka360Available, setIsDoka360Available] = useState(false);
    const [isExistingCustomerDoka360Available, setIsExistingCustomerDoka360Available] = useState(false);
    const [existingRequestId, setExistingRequestId] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const { oidcUser } = useOidcUser();

    const navigate = useNavigate();

    const { mutateAsync: createRequestForExistingCustomerAsync, isPending: isCreateRequestForExistingLoading } =
        useCreateRequestForExistingCustomer();
    const { mutateAsync: createRequestForNewCustomerAsync, isPending: isCreateRequestForNewCustomerLoading } =
        useCreateRequestForNewCustomer();

    const handleContinue = async () => {
        const blockedTenants = import.meta.env.VITE_APP_BLOCKED_TENANTS
            ? (JSON.parse(import.meta.env.VITE_APP_BLOCKED_TENANTS) as string[])
            : [];
        const blockedTenant = blockedTenants.some(
            (tenant) => 'identitytenantid' in oidcUser && oidcUser.identitytenantid === tenant,
        );

        if (blockedTenant) {
            toastNotifications.error({
                title: t('accessDenied'),
                message: t('blockedTenant'),
            });
            return;
        }

        if (existingRequestId) {
            navigate(`/requests/${existingRequestId}`);
            return;
        }

        if (customerType === CustomerType.New && isDoka360Available) {
            navigate('/requests/doka360/available', { state: { countryCode } });
            return;
        }

        if (customerType === CustomerType.Existing && isExistingCustomerDoka360Available) {
            navigate('/requests/doka360/existing', { state: { customerIdentifier: customerNumber, companyName } });
            return;
        }

        try {
            let createdRequest: GetOnboardingRequestDetailResponse | null;
            if (customerType === CustomerType.Existing) {
                createdRequest = await createRequestForExistingCustomerAsync({
                    data: {
                        customerIdentifier: customerNumber,
                        companyName,
                    },
                });
            } else {
                createdRequest = await createRequestForNewCustomerAsync({
                    data: {
                        companyData: {
                            useElectronicInvoice: false,
                            mainAddress: countryId ? { countryConfigurationId: countryId } : undefined,
                        },
                    },
                });
            }

            queryClient.invalidateQueries({ queryKey: getGetRequestsQueryKey() });

            if (createdRequest?.data) {
                navigate(`/requests/${createdRequest.data.id}`);
            }
            toastNotifications.success({ title: t('success'), message: t('dataSaved', { data: t('request') }) });
        } catch {
            toastNotifications.error({ title: t('failed'), message: t('dataNotSaved', { data: t('request') }) });
        }
    };

    const handleCustomerTypeChanged = (value: CustomerType) => {
        setCustomerType(value);
        setExistingRequestId(null);

        if (value === CustomerType.Existing) {
            setCountryId('');
            setCountryCode('');
            setIsDoka360Available(false);
        } else {
            setCustomerNumber('');
            setCompanyName('');
            setIsExistingCustomerDoka360Available(false);
        }
    };

    if (isCreateRequestForExistingLoading || isCreateRequestForNewCustomerLoading) {
        return <LoadingAnimation text={t('creatingRequest')} />;
    }

    return (
        <div className="gap-2xl flex flex-col">
            <div className="gap-lg flex flex-col">
                <h1 className="text-4xl">{t('welcome')}</h1>
                <CustomerSelection
                    customerType={customerType}
                    initialCompanyName={companyName}
                    initialCountryId={countryId}
                    initialCustomerNumber={customerNumber}
                    onCompanyNameChanged={(val) => {
                        setCompanyName(val);
                    }}
                    onCountryChanged={(countryConfigurationId, available, code) => {
                        setCountryId(countryConfigurationId);
                        setIsDoka360Available(available);
                        setCountryCode(code);
                    }}
                    onCustomerNumberChanged={(val, isCountrySupportedInDoka360) => {
                        setCustomerNumber(val);
                        setIsExistingCustomerDoka360Available(isCountrySupportedInDoka360);
                        if (!val) {
                            setExistingRequestId(null);
                        }
                    }}
                    onCustomerTypeChanged={handleCustomerTypeChanged}
                    onRequestAlreadyExists={(request) => setExistingRequestId(request.id)}
                />
            </div>
            <Button
                disabled={
                    (customerType === CustomerType.Existing && (!customerNumber || !companyName.trim()))
                    || (customerType === CustomerType.New && !countryId)
                }
                fullWidth
                onClick={handleContinue}
            >
                {t('continue')}
                <Icon className="p-1" icon="arrow_forward" />
            </Button>
        </div>
    );
};
